import { PermissionEnum } from '@Application/Security/Roles';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import AccountTitle from '@UI/Pages/Home/HomeHeader/AccountTitle';
import HomeFilters from '@UI/Pages/Home/HomeHeader/HomeFilters';
import formatIndicatorPeriod from '@UI/Pages/Home/Helpers/formatIndicatorPeriod';
import useHomeIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope';
import homePeriodSignal from '@UI/Pages/Home/Signals/homePeriodSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useSelector } from '@verclam/synapse';

const stackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const rowStyle = css`
	display: flex;
	gap: 1rem;
	align-items: flex-end;
	justify-content: space-between;
	flex-wrap: wrap;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex-direction: column;
		align-items: stretch;
	}
`;

const titleBlockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

const titleStyle = css`
	margin: 0;
	${theme.typography.heroTitle}
	color: ${theme.colors.ink};
	text-wrap: balance;
`;

const greetingStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.9375rem;
	line-height: 1.4;
	color: ${theme.colors.inkSecondary};
`;

const contextStyle = css`
	margin: 0;
	min-height: 1.5em;
	font-family: ${theme.fonts.mono};
	font-size: 0.71875rem;
	line-height: 1.5;
	color: ${theme.colors.inkMuted};
`;

const pluralize = (count: number, singular: string): string => `${count} ${singular}${count > 1 ? 's' : ''}`;

/** Title, greeting, Périmètre and Période filters, and the line that says what dates and extent they cover. */
const HomeHeader: React.FC = () => {
	const { user } = useStrictAuthenticatedUser();
	const period = useSignalValue(homePeriodSignal);
	const { coverage } = useHomeIndicatorScope();

	const currentWindow = useSelector((state) => {
		const { documents, beneficiaries, costs } = state.indicatorsStore;
		const candidate = documents?.period ?? beneficiaries?.period ?? costs?.period ?? null;

		return candidate !== null && candidate.days === period ? candidate : null;
	});

	// The last window known during this visit stays on screen while a new one loads, so the line
	// never disappears and brings back the layout below it.
	const lastWindow = React.useRef(currentWindow);
	if (currentWindow !== null) {
		lastWindow.current = currentWindow;
	}
	const windowPeriod = currentWindow ?? lastWindow.current;

	const contextParts: string[] = [];

	if (windowPeriod !== null) {
		const formatted = formatIndicatorPeriod(windowPeriod);
		contextParts.push(`du ${formatted.from} au ${formatted.to} (heure de Paris)`);
	}

	if (coverage.facilityCount > 0) {
		contextParts.push(
			pluralize(coverage.organizationCount, 'structure'),
			pluralize(coverage.facilityCount, 'agence'),
		);
	}

	return (
		<div css={stackStyle}>
			<div css={rowStyle}>
				<div css={titleBlockStyle}>
					<h1 css={titleStyle}>
						<WithPermission permission={PermissionEnum.ACCOUNT_READ} fallback="Accueil">
							<AccountTitle />
						</WithPermission>
					</h1>
					<p css={greetingStyle}>{`Bonjour, ${user.firstName}`}</p>
				</div>
				<HomeFilters />
			</div>
			<p css={contextStyle}>{contextParts.join(' · ')}</p>
		</div>
	);
};

export default HomeHeader;
